/**
 * The two portfolio summaries, side by side from xl.
 *
 * `DepositsSummaryCard` is reused verbatim from the deposits page: its own
 * documentation says it never reaches into a payload and computes nothing, so
 * sharing it shares layout and the three states (error+retry, loading, empty)
 * without sharing a single figure. What each side passes in is its own already
 * selected numbers.
 *
 * NO CHILDREN
 * -----------
 * On `/admin/kautionen` the same card carries a table below its stats. Here it
 * carries nothing: the tables, filters, providers and row states stay on that
 * page. A summary that grew a table would be the page a second time.
 *
 * The labels are the page's, to the word. A reader who opens the detail view
 * after reading this tab must find the same number under the same name.
 */
import React from "react";

import DepositsSummaryCard from "../../../pages/admin/deposits/DepositsSummaryCard";
import { chf, chfPrecise } from "../companyFinance/financeFormat";

export default function DepositsTabSummary({
  tenant,
  landlord,
  unresolved = null,
  tenantLoading = false,
  landlordLoading = false,
  tenantError = "",
  landlordError = "",
  tenantEmpty = false,
  landlordEmpty = false,
  onRetryTenant,
  onRetryLandlord,
}) {
  const tenantStats = [
    {
      label: "Gesamtbetrag",
      value: chf(tenant.total),
      emphasis: true,
      testId: "deposits-tab-tenant-stat-total",
    },
    {
      label: "Barkaution",
      value: chf(tenant.cash),
      testId: "deposits-tab-tenant-stat-cash",
    },
    {
      label: "Versicherung",
      value: chf(tenant.insurance),
      testId: "deposits-tab-tenant-stat-insurance",
    },
    {
      label: "Bankdepot",
      value: chf(tenant.bank),
      testId: "deposits-tab-tenant-stat-bank",
    },
    {
      // Zero when nothing is unclassified, rather than an omitted row: the
      // absence of the line and a line reading CHF 0 say different things.
      label: "Nicht zugeordnet",
      value: chf(unresolved ? unresolved.amount : 0),
      testId: "deposits-tab-tenant-stat-unresolved",
    },
  ];

  const landlordStats = [
    {
      label: "Gesamtbetrag",
      value: chf(landlord.total),
      emphasis: true,
      testId: "deposits-tab-landlord-stat-total",
    },
    {
      label: "Versicherung",
      value: chf(landlord.insurance),
      testId: "deposits-tab-landlord-stat-insurance",
    },
    {
      label: "Bankdepot",
      value: chf(landlord.bank),
      testId: "deposits-tab-landlord-stat-bank",
    },
    {
      label: "Bareinzahlung",
      value: chf(landlord.cash),
      testId: "deposits-tab-landlord-stat-cash",
    },
    {
      // Beside the securities, never inside their total: an annual expense and a
      // placed balance are different dimensions three orders of magnitude apart.
      label: "Jahresprämie p.a.",
      value: chfPrecise(landlord.annualPremium),
      testId: "deposits-tab-landlord-stat-premium",
    },
  ];

  return (
    <div
      data-testid="deposits-tab-summary"
      className="grid grid-cols-1 gap-4 xl:grid-cols-2"
    >
      <DepositsSummaryCard
        testId="deposits-tab-tenant-panel"
        title="Mieterkautionen"
        subline="Sicherheiten, die Mieter gegenüber uns gestellt haben"
        stats={tenantStats}
        loading={tenantLoading}
        error={tenantError}
        onRetry={onRetryTenant}
        isEmpty={tenantEmpty}
        emptyMessage="Keine aktuellen Mietersicherheiten"
      />

      <DepositsSummaryCard
        testId="deposits-tab-landlord-panel"
        title="Sicherheitsleistungen bei Vermietern"
        subline="Sicherheiten, die wir gegenüber Vermietern gestellt haben"
        stats={landlordStats}
        loading={landlordLoading}
        error={landlordError}
        onRetry={onRetryLandlord}
        isEmpty={landlordEmpty}
        emptyMessage="Keine Sicherheitsleistungen erfasst"
      />
    </div>
  );
}
